import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListFilter, Search } from 'lucide-react';
import { AdvancedSearchForm } from './AdvancedSearchForm';
import { SearchResults } from './SearchResults';
import { FacetedSearchSidebar } from './FacetedSearchSidebar';
import { searchService } from '../../services/searchService';
import { SearchQuery, SearchResult, SearchResultItem, SearchEntityType, SortOrder } from '../../types/search';
import { Button, Dialog, DialogBody, DialogFooter, EmptyState, ENTITY_TONE, entityKindOf, Page, SegmentedControl } from '../ui';

interface GoogleResultsPageProps {
  initialQuery?: SearchQuery;
  onSearch: (query: SearchQuery) => void;
}

const TYPE_LABELS: Record<string, string> = {
  [SearchEntityType.STAKEHOLDER]: 'Stakeholders',
  [SearchEntityType.ORGANIZATION]: 'Organizations',
  [SearchEntityType.ASSIGNMENT]: 'Roles',
  [SearchEntityType.WIKI_PAGE]: 'Wiki',
  [SearchEntityType.RELATIONSHIP]: 'Relationships',
  [SearchEntityType.MEDIA_ASSET]: 'Media',
};

const ALL_TYPES = Object.values(SearchEntityType);

/** Compares queries by meaning, filling in the defaults the URL leaves out */
function sameQuery(a: SearchQuery, b: SearchQuery): boolean {
  const types = (q: SearchQuery) => (q.entityTypes?.length ? [...q.entityTypes] : [...ALL_TYPES]).sort().join(',');
  return (
    (a.q || '') === (b.q || '') &&
    types(a) === types(b) &&
    (a.organizationType || '') === (b.organizationType || '') &&
    (a.location || '') === (b.location || '') &&
    (a.role || '') === (b.role || '') &&
    (a.department || '') === (b.department || '') &&
    (a.organizationId || '') === (b.organizationId || '') &&
    (a.stakeholderId || '') === (b.stakeholderId || '') &&
    (a.page || 1) === (b.page || 1) &&
    (a.limit || 20) === (b.limit || 20) &&
    (a.sortBy || 'relevance') === (b.sortBy || 'relevance') &&
    (a.sortOrder || SortOrder.DESC) === (b.sortOrder || SortOrder.DESC) &&
    (a.includeHighlights !== false) === (b.includeHighlights !== false)
  );
}

export const GoogleResultsPage: React.FC<GoogleResultsPageProps> = ({
  initialQuery,
  onSearch,
}) => {
  const navigate = useNavigate();
  const [currentQuery, setCurrentQuery] = useState<SearchQuery>(
    initialQuery || {
      entityTypes: Object.values(SearchEntityType),
      page: 1,
      limit: 20,
      sortBy: 'relevance',
      sortOrder: SortOrder.DESC,
      includeHighlights: true,
    }
  );
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTypeFilter, setActiveTypeFilter] = useState<SearchEntityType | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const performSearch = useCallback(async (query: SearchQuery) => {
    setIsLoading(true);
    setError(null);

    try {
      const results = await searchService.search(query);
      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
      setError('An error occurred while searching. Please try again.');
      setSearchResults(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentQuery.q || hasActiveFilters(currentQuery)) {
      performSearch(currentQuery);
    }
  }, [currentQuery, performSearch]);

  // The URL can change from outside this page now that it sits in the app shell (e.g. the header search)
  const currentQueryRef = useRef(currentQuery);
  currentQueryRef.current = currentQuery;
  useEffect(() => {
    if (!initialQuery || sameQuery(currentQueryRef.current, initialQuery)) return;
    setActiveTypeFilter(null);
    setCurrentQuery(initialQuery);
  }, [initialQuery]);

  const hasActiveFilters = (query: SearchQuery): boolean => {
    return !!(
      query.organizationType ||
      query.location ||
      query.role ||
      query.department ||
      query.organizationId ||
      query.stakeholderId ||
      (query.entityTypes && query.entityTypes.length !== Object.values(SearchEntityType).length)
    );
  };

  const handleSearch = (query: SearchQuery) => {
    setActiveTypeFilter(null);
    setCurrentQuery(query);
    onSearch(query);
  };

  const handleClear = () => {
    const clearedQuery: SearchQuery = {
      entityTypes: Object.values(SearchEntityType),
      page: 1,
      limit: 20,
      sortBy: 'relevance',
      sortOrder: SortOrder.DESC,
      includeHighlights: true,
    };
    setActiveTypeFilter(null);
    setCurrentQuery(clearedQuery);
    setSearchResults(null);
    setError(null);
    onSearch(clearedQuery);
  };

  const handlePageChange = (page: number) => {
    const updatedQuery = { ...currentQuery, page };
    setCurrentQuery(updatedQuery);
    onSearch(updatedQuery);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResultClick = (item: SearchResultItem) => {
    const url = searchService.getResultUrl(item.data, item.entityType);
    navigate(url);
  };

  // Entity type tabs from facets
  const typeFacet = searchResults?.facets?.find(f => f.field === 'entityType');
  // The type facet is the segmented control; the rest go in the side column / sheet
  const sideFacets = useMemo(
    () => (searchResults?.facets || []).filter(f => f.field !== 'entityType' && f.values.length > 0),
    [searchResults]
  );
  const activeFacetCount = [
    currentQuery.organizationType,
    currentQuery.location,
    currentQuery.role,
    currentQuery.department,
  ].filter(Boolean).length;

  // Filter displayed results by active type filter (client-side)
  const filteredResults: SearchResult | null = useMemo(() => {
    if (!searchResults) return null;
    if (!activeTypeFilter) return searchResults;

    const filtered = searchResults.items.filter(item => item.entityType === activeTypeFilter);
    return {
      ...searchResults,
      items: filtered,
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / searchResults.limit),
    };
  }, [searchResults, activeTypeFilter]);

  const typeItems = [
    {
      value: 'all',
      label: (
        <>
          All
          <span className="text-[11px] tabular-nums text-neutral-400">{searchResults?.total || 0}</span>
        </>
      ),
    },
    ...(typeFacet?.values || [])
      .filter(v => TYPE_LABELS[v.value])
      .map(v => {
        const kind = entityKindOf(v.value);
        return {
          value: v.value,
          label: (
            <>
              {kind && <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${ENTITY_TONE[kind].solid}`} aria-hidden="true" />}
              {TYPE_LABELS[v.value]}
              <span className="text-[11px] tabular-nums text-neutral-400">{v.count}</span>
            </>
          ),
        };
      }),
  ];

  return (
    <Page>
      <h1 className="sr-only">Search results</h1>

      {/* Search field */}
      <div className="mb-3 max-w-2xl">
        <AdvancedSearchForm
          onSearch={handleSearch}
          onClear={handleClear}
          initialQuery={currentQuery}
          isLoading={isLoading}
        />
      </div>

      {/* Type filter + filters sheet trigger */}
      {((typeFacet && typeFacet.values.length > 0) || sideFacets.length > 0) && (
        <div className="mb-4 flex items-center gap-2">
          {typeFacet && typeFacet.values.length > 0 && (
            <SegmentedControl
              label="Result type"
              className="min-w-0"
              items={typeItems}
              value={activeTypeFilter ?? 'all'}
              onChange={value => setActiveTypeFilter(value === 'all' ? null : (value as SearchEntityType))}
            />
          )}
          {sideFacets.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setFiltersOpen(true)}
              aria-label="Filters"
              className="ml-auto h-10 flex-shrink-0 sm:h-8 lg:hidden"
            >
              <ListFilter className="h-4 w-4" strokeWidth={1.75} />
              <span className="hidden sm:inline">Filters</span>
              {activeFacetCount > 0 && (
                <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] tabular-nums text-white">
                  {activeFacetCount}
                </span>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <p role="alert" className="mb-4 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          {error}
        </p>
      )}

      <div className={sideFacets.length > 0 ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_232px] lg:items-start lg:gap-8' : ''}>
        <div className="min-w-0">
          {(filteredResults || isLoading) && (
            <SearchResults
              results={filteredResults || {
                items: [],
                total: 0,
                page: 1,
                limit: 20,
                totalPages: 0,
                executionTime: 0,
              }}
              onPageChange={handlePageChange}
              onResultClick={handleResultClick}
              isLoading={isLoading}
            />
          )}

          {/* Empty State */}
          {!searchResults && !isLoading && !error && (
            <EmptyState icon={Search} title="Start your search" className="py-16" />
          )}
        </div>

        {sideFacets.length > 0 && (
          <aside className="sticky top-[88px] hidden lg:block" aria-label="Filters">
            <FacetedSearchSidebar facets={sideFacets} currentQuery={currentQuery} onFilterChange={handleSearch} />
          </aside>
        )}
      </div>

      {/* Filters sheet below lg */}
      <Dialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        label="Filters"
        handle
        className="max-h-[85dvh] sm:max-w-sm"
      >
        <DialogBody className="px-3 pt-1 sm:pt-4">
          <FacetedSearchSidebar facets={sideFacets} currentQuery={currentQuery} onFilterChange={handleSearch} />
        </DialogBody>
        <DialogFooter>
          <Button variant="dark" onClick={() => setFiltersOpen(false)} className="h-11 flex-1 sm:h-9 sm:flex-none">
            Done
          </Button>
        </DialogFooter>
      </Dialog>
    </Page>
  );
};
